import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import TranslationNS from '@/utils/Constants/TranslationNS'
import { useAuth } from '@/middlewares/auth/auth-context'
import { MenuIcon } from '@/components/Organizer/OrganizerShell/orgMenuIcons'
import { useOrganizerShell } from '@/components/Organizer/OrganizerShell/OrganizerShellContext'
import { DASHBOARD_TOUR } from './definitions/dashboard.tour'
import { isTourWatched, readLocalTours } from './tourProgress.storage'
import { tourForPath } from './tours.registry'
import useTourStore from './tourStore'

/**
 * « Visite guidée » row at the foot of the organizer side panel (organizers only). Opens a small menu
 * above the row: « Le tableau de bord » replays the side-menu tour, « Cette page » plays the current
 * page's tour when it has one (lot 2), with a dot while the organizer has not seen it yet. Same
 * `osd-item` idiom as the menu rows — icon alone in rail mode, label unfolded. `onBeforeStart` folds
 * the hover overlay / the ≤ 1100 drawer first so a page tour opens on a quiet page (the dashboard
 * tour holds the menu open itself).
 */
export default function TourHelpButton({ onBeforeStart }) {
    const { t } = useTranslation(TranslationNS.TOURS)
    const auth = useAuth()
    const shell = useOrganizerShell()
    const start = useTourStore((state) => state.start)
    const activeId = useTourStore((state) => state.activeId)
    const [open, setOpen] = useState(false)
    const ref = useRef(null)

    const pageTour = tourForPath(shell?.rel)
    // the dot stays after « Don't show again » on the invitation card: the tour itself was never watched
    const pageSeen = pageTour ? isTourWatched(pageTour, auth.user?.systemPreferences?.tours, readLocalTours()) : true

    useEffect(() => {
        if (!open) return undefined
        const onDown = (event) => {
            if (!ref.current?.contains(event.target)) setOpen(false)
        }
        const onKey = (event) => {
            if (event.key !== 'Escape') return
            event.stopPropagation()
            setOpen(false)
        }
        document.addEventListener('mousedown', onDown)
        document.addEventListener('keydown', onKey, true)
        return () => {
            document.removeEventListener('mousedown', onDown)
            document.removeEventListener('keydown', onKey, true)
        }
    }, [open])

    // a tour starting (from here or by itself) closes the menu
    useEffect(() => {
        if (activeId) setOpen(false)
    }, [activeId])

    const play = (id) => {
        setOpen(false)
        if (activeId) return
        onBeforeStart?.()
        start(id)
    }

    return (
        <div className="osd-help" ref={ref}>
            <button
                type="button"
                className="osd-item"
                data-tour="tour-help"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((was) => !was)}
            >
                <MenuIcon name="help" />
                <span className="osd-lbl">{t('Guided tour')}</span>
                {pageTour && !pageSeen ? <span className="osd-help-dot" aria-hidden="true" /> : null}
            </button>
            {open ? (
                <div role="menu" className="osd-help-menu" aria-label={t('Guided tour')}>
                    <button
                        type="button"
                        role="menuitem"
                        className="osd-help-item"
                        onClick={() => play(DASHBOARD_TOUR.id)}
                    >
                        {t('The dashboard')}
                    </button>
                    {pageTour ? (
                        <button
                            type="button"
                            role="menuitem"
                            className="osd-help-item"
                            onClick={() => play(pageTour.id)}
                        >
                            {t('This page')}
                            {!pageSeen ? (
                                <span className="osd-help-dot" role="img" aria-label={t('Not seen yet')} />
                            ) : null}
                        </button>
                    ) : null}
                </div>
            ) : null}
        </div>
    )
}
